<template>
  <div class="coaching-profile max-w-3xl mx-auto p-4 md:p-6">
    <CoachingSubNav active="profile" />

    <header class="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div>
        <h1 class="text-2xl font-semibold text-[var(--color-dark)]">{{ $t('Coaching.Profile.title') }}</h1>
        <p v-if="profile?.lastProfilingAt" class="text-sm text-gray-500 mt-1">
          {{ $t('Coaching.Profile.lastUpdated', { date: formatDate(profile.lastProfilingAt) }) }}
        </p>
      </div>
      <div class="flex flex-wrap gap-2">
        <router-link to="/coaching/profiling">
          <Button
            class="btn-primary-accent"
            unstyled
            :label="$t('Coaching.Profile.startProfiling')"
            icon="pi pi-id-card"
          />
        </router-link>
        <router-link to="/coaching">
          <Button
            :label="$t('Coaching.Profile.startCoaching')"
            icon="pi pi-comments"
            outlined
            class="!border-[var(--color-orange)] !text-[var(--color-orange)]"
          />
        </router-link>
      </div>
    </header>

    <div v-if="loading" class="flex justify-center py-16">
      <ProgressSpinner />
    </div>

    <div v-else-if="error" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <p class="text-red-800">{{ error }}</p>
      <Button class="mt-3" :label="$t('Coaching.retry')" @click="loadProfile" />
    </div>

    <div
      v-else-if="!hasAnyScores"
      class="text-center py-16 px-4 bg-gray-50 border border-gray-200 rounded-xl"
    >
      <i class="pi pi-user text-4xl text-[var(--color-orange)] mb-4" />
      <p class="text-[var(--color-dark)] font-medium mb-2">{{ $t('Coaching.Profile.emptyTitle') }}</p>
      <p class="text-sm text-gray-500 mb-6 max-w-md mx-auto">{{ $t('Coaching.Profile.emptyHint') }}</p>
      <div class="flex flex-wrap justify-center gap-2">
        <router-link to="/coaching/profiling">
          <Button
            class="btn-primary-accent"
            unstyled
            :label="$t('Coaching.Profile.startProfiling')"
            icon="pi pi-id-card"
            icon-pos="right"
          />
        </router-link>
        <router-link to="/coaching">
          <Button
            :label="$t('Coaching.Profile.startCoaching')"
            icon="pi pi-arrow-right"
            icon-pos="right"
            outlined
            class="!border-[var(--color-orange)] !text-[var(--color-orange)]"
          />
        </router-link>
      </div>
    </div>

    <template v-else>
      <section
        v-if="profile.overallInterpretation"
        class="mb-6 p-5 bg-white border border-gray-200 rounded-xl border-l-4 border-l-[var(--color-orange)]"
      >
        <h2 class="text-sm font-semibold text-[var(--color-dark)] uppercase tracking-wide mb-2">
          {{ $t('Coaching.Profile.overallSummary') }}
        </h2>
        <p class="text-[var(--color-dark)] leading-relaxed whitespace-pre-wrap">{{ profile.overallInterpretation }}</p>
      </section>

      <section v-if="profile.assessmentBaseline?.length" class="mb-8">
        <h2 class="text-sm font-semibold text-[var(--color-dark)] uppercase tracking-wide mb-3">
          {{ $t('Coaching.Profile.assessmentBaseline') }}
        </h2>
        <div class="space-y-3">
          <div
            v-for="entry in profile.assessmentBaseline"
            :key="`baseline-${entry.dimensionValue}`"
            class="rounded-xl border p-4"
            :class="cardBorderClass(entry.color)"
          >
            <div class="flex items-center justify-between gap-3 mb-1">
              <h3 class="text-sm font-semibold text-gray-900">{{ entry.dimensionTitle }}</h3>
              <ScoreBadge :score="entry.score" :color="entry.color" />
            </div>
            <p v-if="entry.interpretation" class="text-sm text-gray-600">{{ entry.interpretation }}</p>
            <span class="inline-block mt-2 text-xs text-gray-400">{{ $t('Coaching.Profile.sourceAssessment') }}</span>
          </div>
        </div>
      </section>

      <section v-if="sortedHistory.length" class="mb-8">
        <h2 class="text-sm font-semibold text-[var(--color-dark)] uppercase tracking-wide mb-3">
          {{ $t('Coaching.Profile.coachingHistory') }}
          <span class="text-gray-400 font-normal normal-case">({{ sortedHistory.length }})</span>
        </h2>
        <div class="space-y-2">
          <div
            v-for="(entry, index) in sortedHistory"
            :key="`${entry.sessionId}-${entry.dimensionValue}-${index}`"
            class="rounded-lg border border-gray-200 bg-white overflow-hidden"
          >
            <button
              type="button"
              class="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
              @click="toggleHistoryEntry(index)"
            >
              <div class="min-w-0">
                <p class="text-sm font-medium text-gray-900 truncate">
                  {{ entry.dimensionTitle || $t('Coaching.Profile.unknownDimension') }}
                </p>
                <p class="text-xs text-gray-500 mt-0.5">
                  {{ formatDate(entry.completedAt) }}
                </p>
              </div>
              <i
                class="pi text-gray-400 shrink-0"
                :class="expandedHistoryIndex === index ? 'pi-chevron-up' : 'pi-chevron-down'"
              />
            </button>
            <div
              v-if="expandedHistoryIndex === index && entry.reportSummary"
              class="border-t border-gray-100 px-4 py-3 bg-gray-50/80"
            >
              <p class="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {{ entry.reportSummary }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section class="mb-6">
        <h2 class="text-sm font-semibold text-[var(--color-dark)] uppercase tracking-wide mb-3">
          {{ $t('Coaching.Profile.aiScores') }}
        </h2>

        <div v-if="llmScores.length" class="mb-6 p-4 bg-white border border-gray-200 rounded-xl">
          <div class="space-y-3">
            <div
              v-for="entry in llmScores"
              :key="`bar-${entry.dimensionValue}`"
              class="flex items-center gap-3"
            >
              <span class="text-xs text-gray-600 w-28 sm:w-40 truncate shrink-0" :title="entry.dimensionTitle">
                {{ entry.dimensionTitle }}
              </span>
              <div class="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  class="h-full rounded-full transition-all"
                  :class="barFillClass(entry.color)"
                  :style="{ width: `${Math.min(100, (entry.score / 5) * 100)}%` }"
                />
              </div>
              <span class="text-xs font-semibold text-gray-700 w-8 text-right">{{ entry.score.toFixed(1) }}</span>
            </div>
          </div>
        </div>

        <div class="space-y-3">
          <div
            v-for="entry in displayScores"
            :key="entry.dimensionValue"
            class="rounded-xl border overflow-hidden"
            :class="cardBorderClass(entry.color)"
          >
            <div class="p-4">
              <div class="flex items-start justify-between gap-3 mb-2">
                <div class="min-w-0">
                  <h3 class="text-sm font-semibold text-gray-900">{{ entry.dimensionTitle }}</h3>
                  <span
                    v-if="!entry.isFinal"
                    class="inline-block mt-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full"
                  >
                    {{ $t('Coaching.Profile.preliminary') }}
                  </span>
                </div>
                <ScoreBadge :score="entry.score" :color="entry.color" />
              </div>
              <p class="text-sm text-gray-600 leading-relaxed">{{ entry.interpretation }}</p>
              <span class="inline-block mt-2 text-xs text-gray-400">
                {{ entry.source === 'assessment'
                  ? $t('Coaching.Profile.sourceAssessment')
                  : $t('Coaching.Profile.sourceLlm') }}
              </span>

              <button
                v-if="historyEntriesForDimension(entry.dimensionValue).length"
                type="button"
                class="text-[var(--color-orange)] hover:underline mt-3 flex items-center gap-1.5 text-xs font-medium"
                @click="toggleHistory(entry.dimensionValue)"
              >
                <i class="pi pi-file-edit" />
                {{ $t('Coaching.Profile.coachingReportCount', {
                  count: historyEntriesForDimension(entry.dimensionValue).length,
                }) }}
                <i :class="expandedDimension === entry.dimensionValue ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" />
              </button>
            </div>

            <div
              v-if="expandedDimension === entry.dimensionValue"
              class="border-t border-gray-200/80 px-4 py-3 bg-white/60 space-y-4"
            >
              <div
                v-for="(historyEntry, hIndex) in historyEntriesForDimension(entry.dimensionValue)"
                :key="`dim-hist-${entry.dimensionValue}-${hIndex}`"
                class="pb-3 border-b border-gray-100 last:border-0 last:pb-0"
              >
                <p class="text-xs text-gray-400 mb-2">
                  {{ $t('Coaching.Profile.completedAt', { date: formatDate(historyEntry.completedAt) }) }}
                </p>
                <p class="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {{ historyEntry.reportSummary || $t('Coaching.Profile.noReportExcerpt') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="unscoredDimensions.length" class="mb-6">
        <h2 class="text-sm font-semibold text-[var(--color-dark)] uppercase tracking-wide mb-3">
          {{ $t('Coaching.Profile.notYetScored') }}
        </h2>
        <div class="space-y-2">
          <div
            v-for="dim in unscoredDimensions"
            :key="`pending-${dim.value}`"
            class="rounded-lg border border-dashed border-gray-200 px-4 py-3 text-sm text-gray-500"
          >
            {{ dim.title }}
          </div>
        </div>
      </section>

      <div class="flex flex-wrap justify-center gap-4 sm:gap-6 text-xs text-gray-500 pt-2">
        <span class="flex items-center gap-1.5">
          <span class="w-2.5 h-2.5 rounded-full bg-green-500" />
          {{ $t('Coaching.Profile.legendGreen') }}
        </span>
        <span class="flex items-center gap-1.5">
          <span class="w-2.5 h-2.5 rounded-full bg-yellow-500" />
          {{ $t('Coaching.Profile.legendYellow') }}
        </span>
        <span class="flex items-center gap-1.5">
          <span class="w-2.5 h-2.5 rounded-full bg-red-500" />
          {{ $t('Coaching.Profile.legendRed') }}
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import CoachingSubNav from '@/components/Home/Coaching/CoachingSubNav.vue';
import coachingService from '@/services/coachingService';

const ScoreBadge = {
  name: 'ScoreBadge',
  props: {
    score: { type: Number, required: true },
    color: { type: String, required: true },
  },
  template: `
    <div class="flex items-center gap-2 shrink-0">
      <span class="w-3 h-3 rounded-full" :class="dotClass" />
      <span class="font-bold text-base text-gray-900">{{ score.toFixed(1) }}</span>
    </div>
  `,
  computed: {
    dotClass() {
      const map = {
        green: 'bg-green-500',
        yellow: 'bg-yellow-500',
        red: 'bg-red-500',
      };
      return map[this.color] || 'bg-gray-400';
    },
  },
};

export default {
  name: 'CoachingProfileComponent',
  components: {
    Button,
    CoachingSubNav,
    ProgressSpinner,
    ScoreBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      profile: null,
      coachableDimensions: [],
      expandedDimension: null,
      expandedHistoryIndex: null,
    };
  },
  computed: {
    sortedHistory() {
      return [...(this.profile?.coachingHistory ?? [])].sort(
        (a, b) => new Date(b.completedAt) - new Date(a.completedAt),
      );
    },
    llmScores() {
      const scores = (this.profile?.dimensionScores ?? []).filter((s) => s.source !== 'assessment');
      if (!this.coachableDimensions.length) return scores;
      const order = new Map(this.coachableDimensions.map((d, i) => [d.value, i]));
      return [...scores].sort((a, b) => {
        const ai = order.get(a.dimensionValue) ?? 999;
        const bi = order.get(b.dimensionValue) ?? 999;
        return ai - bi;
      });
    },
    unscoredDimensions() {
      const scored = new Set(this.llmScores.map((s) => s.dimensionValue));
      return this.coachableDimensions.filter((d) => !scored.has(d.value));
    },
    displayScores() {
      if (this.llmScores.length) return this.llmScores;
      return this.profile?.dimensionScores ?? [];
    },
    hasAnyScores() {
      return (this.profile?.dimensionScores?.length ?? 0) > 0
        || (this.profile?.assessmentBaseline?.length ?? 0) > 0
        || (this.profile?.coachingHistory?.length ?? 0) > 0;
    },
  },
  mounted() {
    this.loadProfile();
  },
  methods: {
    async loadProfile() {
      this.loading = true;
      this.error = null;
      try {
        const [profile, dimensions] = await Promise.all([
          coachingService.getProfile(),
          coachingService.listDimensions().catch(() => []),
        ]);
        this.profile = profile ?? {
          dimensionScores: [],
          assessmentBaseline: [],
          coachingHistory: [],
        };
        this.coachableDimensions = dimensions ?? [];
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || this.$t('Coaching.Profile.loadFailed');
      } finally {
        this.loading = false;
      }
    },
    historyEntriesForDimension(dimensionValue) {
      return (this.profile?.coachingHistory ?? [])
        .filter((h) => h.dimensionValue === dimensionValue)
        .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
    },
    toggleHistory(dimensionValue) {
      this.expandedDimension = this.expandedDimension === dimensionValue ? null : dimensionValue;
    },
    toggleHistoryEntry(index) {
      this.expandedHistoryIndex = this.expandedHistoryIndex === index ? null : index;
    },
    formatDate(value) {
      if (!value) return '';
      const locale = this.$i18n?.locale === 'en' ? 'en-GB' : 'de-DE';
      return new Date(value).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
    cardBorderClass(color) {
      const map = {
        green: 'bg-green-50/80 border-green-200',
        yellow: 'bg-yellow-50/80 border-yellow-200',
        red: 'bg-red-50/80 border-red-200',
      };
      return map[color] || 'bg-gray-50 border-gray-200';
    },
    barFillClass(color) {
      const map = {
        green: 'bg-green-500',
        yellow: 'bg-yellow-500',
        red: 'bg-red-500',
      };
      return map[color] || 'bg-[var(--color-orange)]';
    },
  },
};
</script>
